import { apiClient } from '@/shared/api';

import type {
  BasketPricingRequest,
  BasketPricingResponse,
  CheckoutPricingRequest,
  CheckoutPricingResponse,
  CreatePricingSnapshotRequest,
  DiscountRoundingPolicyRequest,
  DiscountRoundingPolicyResponse,
  ExternalProviderConfigRequest,
  ExternalProviderConfigResponse,
  GovernedPricePage,
  GovernedPriceResponse,
  PricingAuditLogPage,
  PricingQuoteResponse,
  PricingSnapshotResponse,
  PriceLifecycleAuditPage,
  PriceLineRequest,
  PriceLineResponse,
  PriceProtectionPolicyRequest,
  PriceProtectionPolicyResponse,
  PriceRejectRequest,
  PricingRulePage,
  PricingRuleRequest,
  PricingRuleResponse,
  PricingRuleUpdateRequest,
  ProductPriceResponse,
  PromoValidationRequest,
  PromoValidationResponse,
  ProviderCredentialRequest,
  ProviderCredentialStatusResponse,
  ResolvedPriceResponse,
  RoundingPolicyRequest,
  RoundingPolicyResponse,
  SalesChannelResponse,
  SalesChannelUpdateRequest,
  TenantPricingConfigRequest,
  TenantPricingConfigResponse,
} from '../model/types';

const BASE = '/api/v1/catalog/pricing';

// ── Runtime pricing ─────────────────────────────────────────────────────────
export const getProductPrice = async (productId: string): Promise<ProductPriceResponse> =>
  (await apiClient.get<ProductPriceResponse>(`${BASE}/products/${productId}`)).data;

export const calculateBasketPricing = async (
  body: BasketPricingRequest,
): Promise<BasketPricingResponse> =>
  (await apiClient.post<BasketPricingResponse>(`${BASE}/calculate-basket`, body)).data;

export const calculateCheckoutPricing = async (
  body: CheckoutPricingRequest,
): Promise<CheckoutPricingResponse> =>
  (await apiClient.post<CheckoutPricingResponse>(`${BASE}/calculate-checkout`, body)).data;

export const validatePromoPricing = async (
  body: PromoValidationRequest,
): Promise<PromoValidationResponse> =>
  (await apiClient.post<PromoValidationResponse>(`${BASE}/validate-promo`, body)).data;

export const createPricingQuote = async (
  body: CheckoutPricingRequest,
): Promise<PricingQuoteResponse> =>
  (await apiClient.post<PricingQuoteResponse>(`${BASE}/quotes`, body)).data;

export const getPricingQuote = async (quoteId: string): Promise<PricingQuoteResponse> =>
  (await apiClient.get<PricingQuoteResponse>(`${BASE}/quotes/${quoteId}`)).data;

export const createPricingSnapshot = async (
  body: CreatePricingSnapshotRequest,
): Promise<PricingSnapshotResponse> =>
  (await apiClient.post<PricingSnapshotResponse>(`${BASE}/snapshots`, body)).data;

export const getPricingSnapshotByOrderId = async (orderId: string): Promise<PricingSnapshotResponse> =>
  (await apiClient.get<PricingSnapshotResponse>(`${BASE}/snapshots/${orderId}`)).data;

export const getPricingAuditLog = async (
  params: { customerId?: string; page?: number; size?: number } = {},
): Promise<PricingAuditLogPage> =>
  (await apiClient.get<PricingAuditLogPage>(`${BASE}/audit`, { params })).data;

// ── Config ──────────────────────────────────────────────────────────────────
export const getPricingConfig = async (): Promise<TenantPricingConfigResponse> =>
  (await apiClient.get<TenantPricingConfigResponse>(`${BASE}/config`)).data;

export const upsertPricingConfig = async (
  body: TenantPricingConfigRequest,
): Promise<TenantPricingConfigResponse> =>
  (await apiClient.put<TenantPricingConfigResponse>(`${BASE}/config`, body)).data;

// ── Rules ─────────────────────────────────────────────────────────────────────
export interface ListPricingRulesParams {
  productId?: string;
  ruleType?: string;
  page?: number;
  size?: number;
}

export const listPricingRules = async (
  params: ListPricingRulesParams = {},
): Promise<PricingRulePage> =>
  (await apiClient.get<PricingRulePage>(`${BASE}/rules`, { params })).data;

export const createPricingRule = async (
  body: PricingRuleRequest,
): Promise<PricingRuleResponse> =>
  (await apiClient.post<PricingRuleResponse>(`${BASE}/rules`, body)).data;

export const getPricingRule = async (ruleId: string): Promise<PricingRuleResponse> =>
  (await apiClient.get<PricingRuleResponse>(`${BASE}/rules/${ruleId}`)).data;

export const updatePricingRule = async (
  ruleId: string,
  body: PricingRuleUpdateRequest,
): Promise<PricingRuleResponse> =>
  (await apiClient.put<PricingRuleResponse>(`${BASE}/rules/${ruleId}`, body)).data;

export const deactivatePricingRule = async (ruleId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/rules/${ruleId}`);
};

// ── External provider configs ─────────────────────────────────────────────────
export const listProviderConfigs = async (): Promise<ExternalProviderConfigResponse[]> =>
  (await apiClient.get<ExternalProviderConfigResponse[]>(`${BASE}/provider-configs`)).data;

export const createProviderConfig = async (
  body: ExternalProviderConfigRequest,
): Promise<ExternalProviderConfigResponse> =>
  (await apiClient.post<ExternalProviderConfigResponse>(`${BASE}/provider-configs`, body)).data;

export const updateProviderConfig = async (
  configId: string,
  body: ExternalProviderConfigRequest,
): Promise<ExternalProviderConfigResponse> =>
  (await apiClient.put<ExternalProviderConfigResponse>(`${BASE}/provider-configs/${configId}`, body))
    .data;

export const deactivateProviderConfig = async (configId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/provider-configs/${configId}`);
};

export const getProviderCredentialStatus = async (
  configId: string,
): Promise<ProviderCredentialStatusResponse> =>
  (await apiClient.get<ProviderCredentialStatusResponse>(`${BASE}/provider-configs/${configId}/credential`))
    .data;

export const setProviderCredential = async (
  configId: string,
  body: ProviderCredentialRequest,
): Promise<void> => {
  await apiClient.put(`${BASE}/provider-configs/${configId}/credential`, body);
};

// ── Approvals ─────────────────────────────────────────────────────────────────
export const listPriceApprovals = async (
  params: { page?: number; size?: number } = {},
): Promise<GovernedPricePage> =>
  (await apiClient.get<GovernedPricePage>(`${BASE}/approvals`, { params })).data;

export const approvePrice = async (priceId: string): Promise<GovernedPriceResponse> =>
  (await apiClient.post<GovernedPriceResponse>(`${BASE}/approvals/${priceId}/approve`)).data;

export const rejectPrice = async (
  priceId: string,
  body: PriceRejectRequest = {},
): Promise<GovernedPriceResponse> =>
  (await apiClient.post<GovernedPriceResponse>(`${BASE}/approvals/${priceId}/reject`, body)).data;

// ── Protection policy + audit ─────────────────────────────────────────────────
export const getPriceProtectionPolicy = async (): Promise<PriceProtectionPolicyResponse> =>
  (await apiClient.get<PriceProtectionPolicyResponse>(`${BASE}/protection-policy`)).data;

export const upsertPriceProtectionPolicy = async (
  body: PriceProtectionPolicyRequest,
): Promise<PriceProtectionPolicyResponse> =>
  (await apiClient.put<PriceProtectionPolicyResponse>(`${BASE}/protection-policy`, body)).data;

export const getPriceLifecycleAudit = async (
  params: { productId?: string; page?: number; size?: number } = {},
): Promise<PriceLifecycleAuditPage> =>
  (await apiClient.get<PriceLifecycleAuditPage>(`${BASE}/lifecycle-audit`, { params })).data;

// ── Sales channels (Phase 2b/3) ─────────────────────────────────────────────
export const listSalesChannels = async (): Promise<SalesChannelResponse[]> =>
  (await apiClient.get<SalesChannelResponse[]>(`${BASE}/sales-channels`)).data;

export const updateSalesChannel = async (
  code: string,
  body: SalesChannelUpdateRequest,
): Promise<SalesChannelResponse> =>
  (await apiClient.put<SalesChannelResponse>(`${BASE}/sales-channels/${code}`, body)).data;

// ── Price lines + resolution (Phase 2b/3) ───────────────────────────────────
export const submitPriceLine = async (body: PriceLineRequest): Promise<PriceLineResponse> =>
  (await apiClient.post<PriceLineResponse>(`${BASE}/price-lines`, body)).data;

export const listPriceLines = async (productId: string): Promise<PriceLineResponse[]> =>
  (await apiClient.get<PriceLineResponse[]>(`${BASE}/price-lines`, { params: { productId } })).data;

export interface ResolvePriceParams {
  productId: string;
  /** adr-pricing-025 — the canonical `CompanyStore.code`; tries the price-list ladder rung first. */
  storeCode?: string;
  channelCode?: string;
  /**
   * epic-pricing-002 Phase 1 (adr-pricing-009) — resolve as this customer group (plan-platform-002
   * WP4.3: the 2 ladder rungs this client-side param previously couldn't address at all —
   * channel+group and group-alone — see `lib/priceGrid.ts`'s `resolveGridCell`).
   */
  customerGroupCode?: string;
  quantity?: number;
  at?: string;
}
export const resolvePrice = async (params: ResolvePriceParams): Promise<ResolvedPriceResponse> =>
  (await apiClient.get<ResolvedPriceResponse>(`${BASE}/price-resolution`, { params })).data;

export const getDiscountRoundingPolicy = async (): Promise<DiscountRoundingPolicyResponse> =>
  (await apiClient.get<DiscountRoundingPolicyResponse>(`${BASE}/discount-rounding-policy`)).data;

export const putDiscountRoundingPolicy = async (
  body: DiscountRoundingPolicyRequest,
): Promise<DiscountRoundingPolicyResponse> =>
  (await apiClient.put<DiscountRoundingPolicyResponse>(`${BASE}/discount-rounding-policy`, body)).data;

// ── Rounding policies (adr-pricing-022, epic-pricing-002 Phase 1) — LIST_PRICE/DISCOUNT_RESULT
// rounding per (currency, scope); deliberately distinct from the discount-rounding policy above.
export const listRoundingPolicies = async (): Promise<RoundingPolicyResponse[]> =>
  (await apiClient.get<RoundingPolicyResponse[]>(`${BASE}/rounding-policies`)).data;

export const putRoundingPolicy = async (
  body: RoundingPolicyRequest,
): Promise<RoundingPolicyResponse> =>
  (await apiClient.put<RoundingPolicyResponse>(`${BASE}/rounding-policies`, body)).data;
